import { useState, type ReactNode } from "react"
import { useQuery } from "@tanstack/react-query"
import { authorizedFetch } from "@/lib/api"
import { getAuth } from "@/lib/auth"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip"
import { InfoIcon } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

// ── Types ──────────────────────────────────────────────────────────────────

type TriStat = { assigned: number; unassigned: number; inactive: number }

type FleetStats = {
  drivers:   TriStat
  semis:     TriStat
  trucks:    TriStat
  coldEquip: TriStat
  mobiles:   { available: number; alistado: number; incomplete: number }
}

type Operator = { id: string; name: string }

const ZERO: TriStat = { assigned: 0, unassigned: 0, inactive: 0 }

// ── Sub-components ──────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  variant = "default",
  isLoading = false,
  info,
}: {
  label: string
  value: number
  variant?: "default" | "info" | "warning" | "danger"
  isLoading?: boolean
  info?: string
}) {
  const descClass =
    variant === "info"
      ? "text-blue-500"
      : variant === "warning"
      ? "text-amber-500"
      : variant === "danger"
      ? "text-destructive"
      : undefined

  return (
    <Card className="@container/card">
      <CardHeader className="relative">
        <CardDescription className={descClass}>{label}</CardDescription>
        <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
          {isLoading ? "—" : value.toLocaleString()}
        </CardTitle>
        {info && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="absolute top-3 right-3 text-muted-foreground/50 hover:text-muted-foreground transition-colors focus:outline-none">
                <InfoIcon className="size-3.5" />
              </TooltipTrigger>
              <TooltipContent side="top" align="end">
                {info}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </CardHeader>
    </Card>
  )
}

function SubSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  )
}

// Shared class that applies the card gradient to child cards
const CARD_GRID_BASE =
  "*:data-[slot=card]:bg-linear-to-t *:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card *:data-[slot=card]:shadow-xs dark:*:data-[slot=card]:bg-card"

// ── Main component ─────────────────────────────────────────────────────────

export function FleetStatsSection() {
  const auth = getAuth()
  const isAdmin = auth?.user.role === "admin"

  const [selectedOperatorId, setSelectedOperatorId] = useState("all")

  // Operators list — admin only, used for the filter dropdown
  const { data: operators = [] } = useQuery({
    queryKey: ["dashboard", "operators"],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/dashboard/operators`)
      const data = (await res.json()) as { operators: Operator[] }
      return data.operators
    },
    enabled: isAdmin,
    staleTime: 60_000,
  })

  const operatorId =
    isAdmin && selectedOperatorId !== "all" ? selectedOperatorId : undefined

  // Fleet statistics
  const { data: stats, isLoading } = useQuery({
    queryKey: ["dashboard", "fleet-stats", operatorId],
    queryFn: async () => {
      const url = new URL(`${API_URL}/api/dashboard/fleet-stats`)
      if (operatorId) url.searchParams.set("operatorId", operatorId)
      const res = await authorizedFetch(url.toString())
      return res.json() as Promise<FleetStats>
    },
    staleTime: 30_000,
  })

  const drivers   = stats?.drivers   ?? ZERO
  const semis     = stats?.semis     ?? ZERO
  const trucks    = stats?.trucks    ?? ZERO
  const coldEquip = stats?.coldEquip ?? ZERO
  const mobiles   = stats?.mobiles   ?? { available: 0, alistado: 0, incomplete: 0 }

  const unitRows = [
    { label: "Semis",        stat: semis     },
    { label: "Camiones",     stat: trucks    },
    { label: "Eq. de Frío",  stat: coldEquip },
  ]

  return (
    <div className="px-4 lg:px-6 flex flex-col gap-6">
      {/* Section header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-base font-semibold">Flota</p>
          <p className="text-sm text-muted-foreground">
            Estado actual de choferes, unidades y móviles
          </p>
        </div>

        {/* Operator filter — admin only */}
        {isAdmin && (
          <Select value={selectedOperatorId} onValueChange={(v) => setSelectedOperatorId(v ?? "all")}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Todos los operadores">
                {selectedOperatorId === "all"
                  ? "Todos los operadores"
                  : (operators.find((op) => op.id === selectedOperatorId)?.name ??
                    selectedOperatorId)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los operadores</SelectItem>
              {operators.map((op) => (
                <SelectItem key={op.id} value={op.id}>
                  {op.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* ── Choferes ────────────────────────────────────────────────── */}
      <SubSection title="Choferes">
        <div className={`grid grid-cols-1 gap-4 @xl/main:grid-cols-3 ${CARD_GRID_BASE}`}>
          <StatCard label="Asignados"   value={drivers.assigned}   isLoading={isLoading} />
          <StatCard label="Sin asignar" value={drivers.unassigned} isLoading={isLoading} variant="warning" />
          <StatCard label="No activos"  value={drivers.inactive}   isLoading={isLoading} variant="danger" />
        </div>
      </SubSection>

      {/* ── Unidades ────────────────────────────────────────────────── */}
      <SubSection title="Unidades">
        <div className={`*:data-[slot=card]:bg-linear-to-t *:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card *:data-[slot=card]:shadow-xs dark:*:data-[slot=card]:bg-card`}>
          <Card>
            <CardContent className="pt-4 pb-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left pb-2 font-medium text-muted-foreground w-1/4">
                      Tipo
                    </th>
                    <th className="text-right pb-2 font-medium text-muted-foreground">
                      Asignados
                    </th>
                    <th className="text-right pb-2 font-medium text-amber-500">
                      Sin asignar
                    </th>
                    <th className="text-right pb-2 font-medium text-destructive">
                      No activos
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {unitRows.map(({ label, stat }, idx) => (
                    <tr
                      key={label}
                      className={idx < unitRows.length - 1 ? "border-b" : ""}
                    >
                      <td className="py-3 font-medium">{label}</td>
                      <td className="py-3 text-right tabular-nums font-semibold">
                        {isLoading ? "—" : stat.assigned.toLocaleString()}
                      </td>
                      <td className="py-3 text-right tabular-nums font-semibold text-amber-500">
                        {isLoading ? "—" : stat.unassigned.toLocaleString()}
                      </td>
                      <td className="py-3 text-right tabular-nums font-semibold text-destructive">
                        {isLoading ? "—" : stat.inactive.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </SubSection>

      {/* ── Móviles ─────────────────────────────────────────────── */}
      <SubSection title="Móviles">
        <div className={`grid grid-cols-1 gap-4 @xl/main:grid-cols-3 ${CARD_GRID_BASE}`}>
          <StatCard label="Disponibles" value={mobiles.available}  isLoading={isLoading} info="Chofer activo + camión activo + semi activo" />
          <StatCard label="Alistados"   value={mobiles.alistado}   isLoading={isLoading} variant="info"    info="Camión activo sin chofer asignado" />
          <StatCard label="Incompletos" value={mobiles.incomplete} isLoading={isLoading} variant="warning" info="Falta algún componente o hay uno inactivo / eliminado" />
        </div>
      </SubSection>
    </div>
  )
}
